import {
  type Configuration,
  DirectoryEntityType,
  type ListMicrosoft365GroupMembersSort,
  type Snapshot,
  type SearchDirectoryEntitiesResponse,
  type Microsoft365User,
  type Microsoft365SharePointSite,
  type SaasEntity,
  GetGwsBackupFilterApi,
  ListGoogleWorkspaceGroupsApi,
  ListGoogleWorkspaceOrgUnitsApi,
  ListInventoryItemSnapshotsApi,
  ListMicrosoft365UsersApi,
  ListMicrosoft365GroupsApi,
  ListMicrosoft365GroupMembersApi,
  GetMicrosoft365BackupScopeApi,
  type Microsoft365PolicyConfig,
  ListMicrosoft365SharePointSitesApi,
  ListSaasEntitiesApi,
  SaasEntityType,
  UpdateShouldFullBackupResourceApi,
  FileExplorerListPathApi,
  FileExplorerCountWorkspaceEntitiesApi,
  SearchDirectoryEntitiesApi,
  ListGroupConversationsApi,
} from '@repo/api-gw-sdk';
import { mutate } from 'swr';

import type { GWSnapshot } from '@/app/(dashboard)/saas-inventory/google-workspace/snapshotTypes';
import { dayjs } from '@/utils/dayjs';

import type { HttpClient } from './httpClient';

const backupSettingsUrl = '/inventory/backup-settings';
const orgUnitsUrl = '/saas-inventory/google-workspace/org-units';
const groupsUrl = '/saas-inventory/google-workspace/groups';
const snapshotsBaseUrl = '/saas-inventory/google-workspace';
const entraIdBaseUrl = '/saas-inventory/entra-id';
const m365UsersUrl = '/saas-inventory/microsoft-365/users';
const m365GroupsUrl = '/saas-inventory/microsoft-365/groups';
const m365GroupMembersUrl = '/saas-inventory/microsoft-365/group-members';
const m365SharePointSitesUrl = '/saas-inventory/microsoft-365/sharepoint-sites';
const m365BackupScopeUrl = '/saas-inventory/microsoft-365/backup-scope';
const m365EntitiesUrl = '/saas-inventory/microsoft-365/entities';

export type Microsoft365UserListItem = Microsoft365User & {
  terminated?: boolean;
};
export type Microsoft365SharePointSiteListItem = Microsoft365SharePointSite & {
  terminated?: boolean;
};

// userId/siteId map to externalId (the Graph id), not entity.id: backup-scope coverage,
// threat lookups and the browse-index prefixes all key on the Graph id.
function mapSaasEntityToMicrosoft365User(
  entity: SaasEntity
): Microsoft365UserListItem {
  const user = entity.properties?.microsoft365User;
  return {
    userId: entity.externalId,
    displayName: entity.displayName,
    userPrincipalName: user?.userPrincipalName,
    mail: user?.email,
    accountEnabled: user?.accountEnabled,
    hasMailbox: user?.hasMailbox,
    hasOnedrive: user?.hasOnedrive,
    mailboxType: user?.mailboxType,
    licenseStatus: user?.licenseStatus,
    licenses: user?.licenses,
    terminated: entity.terminated,
  };
}

function mapSaasEntityToSharePointSite(
  entity: SaasEntity
): Microsoft365SharePointSiteListItem {
  return {
    siteId: entity.externalId,
    name: entity.displayName,
    webUrl: entity.properties?.sharePointSite?.webUrl,
    terminated: entity.terminated,
  };
}

// listSaasEntities decodes its pageToken as a base64 `<prefix>|pageIndex`. Encode it
// deterministically, not via encodePageToken, whose random uuid would change the SWR
// key every render and refetch in a loop.
function toEntitiesPageToken(pageToken?: string): string | undefined {
  return pageToken ? btoa(`page|${Number(pageToken)}`) : undefined;
}

const entityRequest = (type: SaasEntityType, search?: string) => ({
  filters: { type: { _in: [type] }, ...(search ? { search } : {}) },
});

// Matches the gateway's maxM365ListIds cap (m365_directory_impl.go) — a saved selection larger
// than one chunk is split across multiple requests instead of failing the whole resolve.
const M365_IDS_CHUNK_SIZE = 500;

const chunkIds = (ids: string[]): string[][] => {
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += M365_IDS_CHUNK_SIZE) {
    chunks.push(ids.slice(i, i + M365_IDS_CHUNK_SIZE));
  }
  return chunks;
};

interface DirectorySearchArgs {
  resourceId: string;
  snapshotId?: string;
  query?: string;
  entityType?: DirectoryEntityType;
  entityId?: string;
  pageIndex: number;
  pageSize: number;
}

function snapshotToGWSnapshot(
  snapshot: Snapshot,
  isLatest: boolean
): GWSnapshot {
  const date = (snapshot.pointInTime ?? snapshot.createdTime).toISOString();
  return {
    id: snapshot.id,
    date,
    label: `${dayjs(date).format('MMM D, YYYY')}${isLatest ? ' (Latest)' : ''}`,
    sizeBytes: snapshot.fullSizeBytes ?? 0,
    userCount: 0,
    sharedDriveCount: 0,
    userDailyBudgetExceededItemsCount:
      snapshot.userDailyBudgetExceededItemsCount,
  };
}

export const saasInventory = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const getBackupSettingsApi = new GetGwsBackupFilterApi(config);
  const updateBackupSettingsApi = new UpdateShouldFullBackupResourceApi(config);
  const listOrgUnitsApi = new ListGoogleWorkspaceOrgUnitsApi(config);
  const listGroupsApi = new ListGoogleWorkspaceGroupsApi(config);
  const listSnapshotsApi = new ListInventoryItemSnapshotsApi(config);
  const listM365UsersApi = new ListMicrosoft365UsersApi(config);
  const listM365GroupsApi = new ListMicrosoft365GroupsApi(config);
  const listM365GroupMembersApi = new ListMicrosoft365GroupMembersApi(config);
  const listM365SharePointSitesApi = new ListMicrosoft365SharePointSitesApi(
    config
  );
  const m365BackupScopeApi = new GetMicrosoft365BackupScopeApi(config);
  const listSaasEntitiesApi = new ListSaasEntitiesApi(config);
  const listPathApi = new FileExplorerListPathApi(config);
  const countWorkspaceEntitiesApi = new FileExplorerCountWorkspaceEntitiesApi(
    config
  );
  const searchDirectoryApi = new SearchDirectoryEntitiesApi(config);
  const listGroupConversationsApi = new ListGroupConversationsApi(config);

  // Every workspace kind lists snapshots through the same inventory endpoint and
  // differs only in its SWR cache namespace, so each passes its own baseUrl.
  const useSnapshotsRequest = (baseUrl: string, resourceId: string | null) =>
    httpClient.execute(
      listSnapshotsApi.listInventoryItemSnapshots.bind(listSnapshotsApi),
      resourceId ? baseUrl : null,
      'snapshots',
      currentProjectId,
      resourceId ?? '',
      0,
      100,
      'eon'
    );

  // Reads the per-snapshot directory_entities index, so it reacts to the selected
  // snapshot and is empty for pre-index snapshots — unlike the live /groups/list
  // discovery behind useGroups. `emptyOnMissingIndex` turns the 404 a snapshot
  // without that index returns into an empty list instead of an error.
  const useDirectorySearchRequest = (
    baseUrl: string,
    {
      resourceId,
      snapshotId,
      query,
      entityType,
      entityId,
      pageIndex,
      pageSize,
    }: DirectorySearchArgs,
    { emptyOnMissingIndex = false }: { emptyOnMissingIndex?: boolean } = {}
  ) =>
    httpClient.execute(
      (
        ...args: Parameters<typeof searchDirectoryApi.searchDirectoryEntities>
      ) =>
        searchDirectoryApi.searchDirectoryEntities(...args).catch((error) => {
          if (emptyOnMissingIndex && error.code === 404) {
            const noIndex: SearchDirectoryEntitiesResponse = {
              data: [],
              pagination: { recordsCount: 0 },
            };
            return noIndex;
          }
          throw error;
        }),
      resourceId && snapshotId ? baseUrl : null,
      'directorySearch',
      currentProjectId,
      resourceId,
      { snapshotId: snapshotId ?? '', query, entityType, entityId },
      pageIndex,
      pageSize
    );

  return {
    googleWorkspace: {
      useSnapshots: (resourceId: string | null) => {
        const result = useSnapshotsRequest(snapshotsBaseUrl, resourceId);
        const snapshots: GWSnapshot[] = (result.data?.data ?? []).map(
          (s: Snapshot, i: number) => snapshotToGWSnapshot(s, i === 0)
        );
        return {
          data: snapshots,
          isLoading: result.isLoading,
        };
      },
      useListPath: (
        resourceId: string,
        prefix: string,
        pageSize: number,
        snapshotId?: string,
        pageToken?: string,
        queryExecutionId?: string,
        sortField?: string,
        sortDir?: string,
        includeTotalCount?: boolean
      ) =>
        httpClient.execute(
          listPathApi.fileExplorerListPath.bind(listPathApi),
          resourceId ? snapshotsBaseUrl : null,
          'listPath',
          currentProjectId,
          resourceId,
          prefix,
          pageSize,
          pageToken,
          queryExecutionId,
          undefined,
          snapshotId,
          sortField,
          sortDir,
          includeTotalCount
        ),
      // Gated on resourceId only: with no snapshotId, all-snapshots mode counts
      // across valid snapshots server-side.
      useEntityCount: (
        resourceId: string,
        prefix: string,
        snapshotId?: string
      ) =>
        httpClient.execute(
          countWorkspaceEntitiesApi.fileExplorerCountWorkspaceEntities.bind(
            countWorkspaceEntitiesApi
          ),
          resourceId ? snapshotsBaseUrl : null,
          'entityCount',
          currentProjectId,
          resourceId,
          prefix,
          snapshotId
        ),
      useDirectorySearch: (args: DirectorySearchArgs) =>
        useDirectorySearchRequest(snapshotsBaseUrl, args, {
          emptyOnMissingIndex: true,
        }),
      // Lists the emails addressed to a group (distribution list), deduplicated to one row per
      // message across every member mailbox. Omitting snapshotId spans every snapshot.
      useGroupConversations: ({
        resourceId,
        groupEmail,
        snapshotId,
        pageIndex,
        pageSize,
        sortField,
        sortAsc,
      }: {
        resourceId: string;
        groupEmail: string;
        snapshotId?: string;
        pageIndex: number;
        pageSize: number;
        sortField?: string;
        sortAsc?: boolean;
      }) =>
        httpClient.execute(
          listGroupConversationsApi.listGroupConversations.bind(
            listGroupConversationsApi
          ),
          resourceId && groupEmail ? snapshotsBaseUrl : null,
          'groupConversations',
          currentProjectId,
          resourceId,
          { groupEmail, snapshotId, sortField, sortAsc },
          pageIndex,
          pageSize
        ),
      useOrgUnits: (connectorId: string) =>
        httpClient.execute(
          listOrgUnitsApi.listGoogleWorkspaceOrgUnits.bind(listOrgUnitsApi),
          orgUnitsUrl,
          connectorId,
          currentProjectId,
          connectorId
        ),
      useGroups: (connectorId: string) =>
        httpClient.execute(
          listGroupsApi.listGoogleWorkspaceGroups.bind(listGroupsApi),
          groupsUrl,
          connectorId,
          currentProjectId,
          connectorId
        ),
      useBackupSettings: (resourceId: string) =>
        httpClient.execute(
          getBackupSettingsApi.getGwsBackupFilter.bind(getBackupSettingsApi),
          backupSettingsUrl,
          resourceId,
          currentProjectId,
          resourceId
        ),
      updateBackupSettings: (resourceId: string, gwsFilterJson: string) =>
        updateBackupSettingsApi
          .updateShouldFullBackupResource(currentProjectId, resourceId, {
            gwsFilterJson,
          })
          .then(() =>
            mutate([
              backupSettingsUrl,
              resourceId,
              currentProjectId,
              resourceId,
            ])
          ),
    },
    entraId: {
      useSnapshots: (resourceId: string | null) =>
        useSnapshotsRequest(entraIdBaseUrl, resourceId),
      useDirectorySearch: (args: DirectorySearchArgs) =>
        useDirectorySearchRequest(entraIdBaseUrl, args),
      useDirectoryEdges: ({
        resourceId,
        snapshotId,
        entityId,
      }: {
        resourceId: string;
        snapshotId?: string;
        entityId?: string;
      }) =>
        httpClient.execute(
          searchDirectoryApi.listDirectoryEdges.bind(searchDirectoryApi),
          resourceId && snapshotId && entityId ? entraIdBaseUrl : null,
          'directoryEdges',
          currentProjectId,
          resourceId,
          { snapshotId: snapshotId ?? '', entityId: entityId ?? '' }
        ),
      useDirectoryEntity: ({
        resourceId,
        snapshotId,
        entityId,
        entityType,
      }: {
        resourceId: string;
        snapshotId?: string;
        entityId?: string;
        entityType?: DirectoryEntityType;
      }) =>
        httpClient.execute(
          searchDirectoryApi.getDirectoryEntity.bind(searchDirectoryApi),
          resourceId && snapshotId && entityId && entityType
            ? entraIdBaseUrl
            : null,
          'directoryEntity',
          currentProjectId,
          resourceId,
          {
            snapshotId: snapshotId ?? '',
            entityId: entityId ?? '',
            entityType:
              entityType ??
              DirectoryEntityType.DIRECTORY_ENTITY_TYPE_UNSPECIFIED,
          }
        ),
    },
    microsoft365: {
      // Search and pagination run server-side (the gateway filters in the DB and
      // returns nextPageToken); every distinct (search, page) tuple is its own
      // SWR key. Omitting pageSize keeps the full-listing behavior. enabled=false
      // skips the request entirely (null SWR key) so callers can mount the hook
      // unconditionally and only fetch when their UI needs the data.
      useUsers: (
        connectorId: string,
        search?: string,
        pageSize?: number,
        pageToken?: string,
        enabled = true,
        useEntities = false
      ) => {
        const legacy = httpClient.execute(
          listM365UsersApi.listMicrosoft365Users.bind(listM365UsersApi),
          enabled && !useEntities ? m365UsersUrl : null,
          connectorId,
          currentProjectId,
          connectorId,
          pageToken,
          pageSize,
          search ? { search } : undefined
        );
        const entities = httpClient.execute(
          listSaasEntitiesApi.listSaasEntities.bind(listSaasEntitiesApi),
          enabled && useEntities ? m365EntitiesUrl : null,
          `${connectorId}|users`,
          currentProjectId,
          connectorId,
          toEntitiesPageToken(pageToken),
          pageSize,
          entityRequest(SaasEntityType.SAAS_ENTITY_TYPE_USER, search)
        );

        if (!useEntities) {
          return legacy;
        }
        return {
          ...entities,
          data: entities.data
            ? {
                users: (entities.data.entities ?? []).map(
                  mapSaasEntityToMicrosoft365User
                ),
                totalCount: entities.data.totalCount,
                nextPageToken: entities.data.nextToken,
              }
            : undefined,
        };
      },
      useGroups: (
        connectorId: string,
        search?: string,
        pageSize?: number,
        pageToken?: string,
        enabled = true
      ) =>
        httpClient.execute(
          listM365GroupsApi.listMicrosoft365Groups.bind(listM365GroupsApi),
          enabled ? m365GroupsUrl : null,
          connectorId,
          currentProjectId,
          connectorId,
          pageToken,
          pageSize,
          search ? { search } : undefined
        ),
      // Resolves specific ids regardless of pagination. Part of the SWR key, so pass them sorted.
      // Chunked so a selection past the gateway's per-request id cap still resolves in full.
      useGroupsByIds: (connectorId: string, ids: string[]) =>
        httpClient.execute(
          async (cId: string, idsArg: string[]) => {
            const responses = await Promise.all(
              chunkIds(idsArg).map((idsChunk) =>
                listM365GroupsApi.listMicrosoft365Groups(
                  currentProjectId,
                  cId,
                  undefined,
                  undefined,
                  { ids: idsChunk }
                )
              )
            );
            return { groups: responses.flatMap((r) => r.groups ?? []) };
          },
          ids.length > 0 ? m365GroupsUrl : null,
          connectorId,
          connectorId,
          ids
        ),
      // Search, sort and pagination all run server-side, same as useUsers; the
      // pageToken is the page index in decimal. Omitting pageSize keeps the
      // full-listing behavior.
      useGroupMembers: (
        connectorId: string,
        groupId: string | null,
        search?: string,
        pageSize?: number,
        pageToken?: string,
        sort?: ListMicrosoft365GroupMembersSort
      ) =>
        httpClient.execute(
          listM365GroupMembersApi.listMicrosoft365GroupMembers.bind(
            listM365GroupMembersApi
          ),
          connectorId && groupId ? m365GroupMembersUrl : null,
          groupId ?? '',
          currentProjectId,
          connectorId,
          groupId ?? '',
          pageToken,
          pageSize,
          search || sort ? { search, sort } : undefined
        ),
      // Which users and sites a backup policy currently covers. Pass a draft
      // config to preview an unsaved selection instead of the tenant's policies.
      useBackupScope: (
        connectorId: string,
        config?: Microsoft365PolicyConfig,
        enabled = true
      ) =>
        httpClient.execute(
          m365BackupScopeApi.getMicrosoft365BackupScope.bind(
            m365BackupScopeApi
          ),
          enabled && connectorId ? m365BackupScopeUrl : null,
          `${connectorId}|${config ? JSON.stringify(config) : 'saved'}`,
          currentProjectId,
          connectorId,
          config ? { config } : undefined
        ),
      useSharePointSites: (
        connectorId: string,
        search?: string,
        pageSize?: number,
        pageToken?: string,
        enabled = true,
        useEntities = false
      ) => {
        const legacy = httpClient.execute(
          listM365SharePointSitesApi.listMicrosoft365SharePointSites.bind(
            listM365SharePointSitesApi
          ),
          enabled && !useEntities ? m365SharePointSitesUrl : null,
          connectorId,
          currentProjectId,
          connectorId,
          pageToken,
          pageSize,
          search ? { search } : undefined
        );
        const entities = httpClient.execute(
          listSaasEntitiesApi.listSaasEntities.bind(listSaasEntitiesApi),
          enabled && useEntities ? m365EntitiesUrl : null,
          `${connectorId}|sharepoint-sites`,
          currentProjectId,
          connectorId,
          toEntitiesPageToken(pageToken),
          pageSize,
          entityRequest(SaasEntityType.SAAS_ENTITY_TYPE_SHAREPOINT_SITE, search)
        );

        if (!useEntities) {
          return legacy;
        }
        return {
          ...entities,
          data: entities.data
            ? {
                sharepointSites: (entities.data.entities ?? []).map(
                  mapSaasEntityToSharePointSite
                ),
                totalCount: entities.data.totalCount,
                nextPageToken: entities.data.nextToken,
              }
            : undefined,
        };
      },
      // Resolves specific ids regardless of pagination. Part of the SWR key, so pass them sorted.
      // Chunked so a selection past the gateway's per-request id cap still resolves in full.
      useSharePointSitesByIds: (connectorId: string, ids: string[]) =>
        httpClient.execute(
          async (cId: string, idsArg: string[]) => {
            const responses = await Promise.all(
              chunkIds(idsArg).map((idsChunk) =>
                listM365SharePointSitesApi.listMicrosoft365SharePointSites(
                  currentProjectId,
                  cId,
                  undefined,
                  undefined,
                  { ids: idsChunk }
                )
              )
            );
            return {
              sharepointSites: responses.flatMap(
                (r) => r.sharepointSites ?? []
              ),
            };
          },
          ids.length > 0 ? m365SharePointSitesUrl : null,
          connectorId,
          connectorId,
          ids
        ),
    },
  };
};
